import { useEffect } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { useLocation, useNavigate } from "react-router";

import { useResendOtp } from "@/features/identity/hooks/useResendOtp";
import { useVerifyOtp } from "@/features/identity/hooks/useVerifyOtp";
import { otpSchema, type OtpFormValues } from "@/features/identity/schemas/auth.schemas";
import { Button } from "@/components/ui/button";
import { OtpInput } from "@/components/ui/otp-input";
import { showToast } from "@/components/ui/toast";
import { GUEST_ONLY_ROUTES, PUBLIC_ROUTES } from "@/constants/routes";
import { ApiError } from "@/types/api";

interface OtpLocationState {
  email?: string;
}

/**
 * docs/ui/routes.md: "Not a bookmarkable/directly-linkable route in
 * practice — requires state carried from /register or /login... Direct
 * navigation here with no prior context redirects to /register." That
 * fallback is implemented below, not just documented.
 */
export function OtpVerificationPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const email = (location.state as OtpLocationState | null)?.email;

  const verifyOtpMutation = useVerifyOtp();
  const resendOtpMutation = useResendOtp();

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<OtpFormValues>({
    resolver: zodResolver(otpSchema),
    defaultValues: { code: "" },
  });

  useEffect(() => {
    if (!email) {
      navigate(GUEST_ONLY_ROUTES.register, { replace: true });
    }
  }, [email, navigate]);

  if (!email) {
    return null;
  }

  // A fresh `const` of type `string` (not a narrowed `string | undefined`)
  // so the nested function declarations below — which TypeScript can't
  // assume run only after the guard above — see a plain `string`.
  const verifiedEmail = email;

  function onSubmit(values: OtpFormValues) {
    verifyOtpMutation.mutate(
      { email: verifiedEmail, code: values.code },
      {
        onSuccess: () => navigate(PUBLIC_ROUTES.home, { replace: true }),
        onError: (error) => {
          showToast.error(error instanceof ApiError ? error.message : "Verification failed.");
        },
      },
    );
  }

  function handleResend() {
    resendOtpMutation.mutate(
      { email: verifiedEmail },
      {
        onSuccess: () => showToast.success(`A new code was sent to ${verifiedEmail}.`),
        onError: (error) => {
          showToast.error(error instanceof ApiError ? error.message : "Couldn't resend the code.");
        },
      },
    );
  }

  return (
    <div className="flex min-h-[calc(100vh-4rem)] items-center justify-center px-6 py-12">
      <div className="w-full max-w-[400px] space-y-6 text-center">
        <div className="space-y-1">
          <h1 className="text-h1 text-foreground">Verify your email</h1>
          <p className="text-body text-muted-foreground">
            Enter the 6-digit code we sent to{" "}
            <span className="font-medium text-foreground">{email}</span>.
          </p>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} noValidate className="space-y-4">
          <Controller
            name="code"
            control={control}
            render={({ field }) => (
              <div className="flex flex-col items-center gap-1.5">
                <OtpInput
                  value={field.value}
                  onChange={field.onChange}
                  hasError={Boolean(errors.code)}
                />
                {errors.code && (
                  <p className="text-caption text-destructive" role="alert">
                    {errors.code.message}
                  </p>
                )}
              </div>
            )}
          />

          <Button type="submit" fullWidth isLoading={verifyOtpMutation.isPending}>
            Verify
          </Button>
        </form>

        <button
          type="button"
          onClick={handleResend}
          disabled={resendOtpMutation.isPending}
          className="text-body-sm text-primary underline-offset-4 hover:underline disabled:opacity-50"
        >
          {resendOtpMutation.isPending ? "Sending..." : "Resend code"}
        </button>
      </div>
    </div>
  );
}
